import React, { FC, useEffect, useState } from 'react';

import { Typography } from '@mui/material';
import { Fit, Layout, useRive } from '@rive-app/react-canvas';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { HUMANIZER_MODE_V2 } from '~/modules/tools/views/AiHumanizerV4/constants';
import { Typographies } from '~/theme/typography';
import { onAssetLoad } from '~/utils/animations';

import styles from './styles.module.scss';
import AnimationBypassSrc from '../../assets/humanizer_goal_bypass.riv';
import AnimationHumanSrc from '../../assets/humanizer_goal_human.riv';

const getTexts = (t: TFunction): { runName: string; text: string }[] => [
  {
    runName: 'HumanWritten',
    text: t(
      'modal:feature_unlock.humanizer_goal_animation_text[0]',
      '100% Human Written',
    ),
  },
];

const srcMap = {
  [HUMANIZER_MODE_V2.Detectors]: AnimationBypassSrc,
  [HUMANIZER_MODE_V2.Human]: AnimationHumanSrc,
};

type Props = {
  humanizerMode?: HUMANIZER_MODE_V2;
};

const HumanizerGoalAnimation: FC<Props> = ({
  humanizerMode = HUMANIZER_MODE_V2.Human,
}) => {
  const { t } = useTranslation();

  const initialMode =
    humanizerMode === HUMANIZER_MODE_V2.Detectors
      ? HUMANIZER_MODE_V2.Detectors
      : HUMANIZER_MODE_V2.Human;

  const [currentAnimation, setCurrentAnimation] = useState(initialMode);

  const { rive, RiveComponent } = useRive({
    src: srcMap[currentAnimation],
    autoplay: true,
    layout: new Layout({
      fit: Fit.Cover,
    }),
    assetLoader: onAssetLoad,
  });

  useEffect(() => {
    if (rive) {
      getTexts(t).forEach(({ runName, text }) => {
        rive.setTextRunValue(runName, text);
      });
    }
  }, [rive]);

  return (
    <div className={styles.animation}>
      <div className={styles.controls}>
        <button
          className={clsx(styles.button, {
            [styles.is_active]:
              currentAnimation === HUMANIZER_MODE_V2.Detectors,
          })}
          onClick={() => setCurrentAnimation(HUMANIZER_MODE_V2.Detectors)}
        >
          {t('general:bypass_detectors', 'Bypass Detectors')}
        </button>

        <button
          className={clsx(styles.button, {
            [styles.is_active]: currentAnimation === HUMANIZER_MODE_V2.Human,
          })}
          onClick={() => setCurrentAnimation(HUMANIZER_MODE_V2.Human)}
        >
          {t('general:sound_human', 'Sound Human')}
        </button>
      </div>

      <div className={styles.animation}>
        <RiveComponent key={currentAnimation} />
      </div>

      <Typography
        variant={Typographies.LABEL_LARGE}
        component="div"
        className={styles.subtitle}
      >
        {currentAnimation === HUMANIZER_MODE_V2.Detectors
          ? t(
              'highlight:ai_humanizer.bypass_detector.text_cut',
              'Aggressive setting with the primary goal of bypassing the strictest detectors',
            )
          : t(
              'highlight:ai_humanizer.sound_human.text',
              'Light humanization mode aimed to make text appear as human written but not necessarily pass the strictest detectors',
            )}
      </Typography>
    </div>
  );
};

export default HumanizerGoalAnimation;
